import { Injectable } from '@angular/core';
import { CompanyAdministratorSubscription, User } from '@movinmotion/conf-shared';

/**
 * Construit l'identifiant composite du formulaire de ticket Zoho Desk (widget ASAP).
 * La nouvelle API de prefill cible un layout précis via la clé `<departmentId>&&&<layoutId>`.
 */
export function buildZohoFormKey(departmentId: string, layoutId: string): string {
  return `${departmentId}&&&${layoutId}`;
}

/**
 * Couples departmentId / layoutId Zoho Desk, un par application (fournis par les OPS, cf MOV-19588).
 * Ces IDs sont identiques sur tous les environnements de déploiement (une seule org Zoho Desk).
 */
export const ZOHO_FORM_KEY_SOCIAL = buildZohoFormKey('40949000000258412', '40949000000260703');
export const ZOHO_FORM_KEY_COMPTA = buildZohoFormKey('40949000004657045', '40949000004659378');
export const ZOHO_FORM_KEY_ENTREPRISE = buildZohoFormKey('40949000000007061', '40949000000023011');
export const ZOHO_FORM_KEY_TALENTS = buildZohoFormKey('40949000009539424', '40949000009551685');
export const ZOHO_FORM_KEY_SALARIE = buildZohoFormKey('40949000000265053', '40949000000267344');

/**
 * API names des champs du formulaire de ticket à préremplir (widget ASAP).
 * Fournis par les OPS (cf MOV-19588) — ces noms viennent de la section "List Fields" du layout Zoho Desk.
 *
 * `ZOHO_FIELD_CONTACT` / `ZOHO_FIELD_EMAIL` existent sur les deux formulaires (employeur et salarié) ;
 * `ZOHO_FIELD_PHONE` / `ZOHO_FIELD_COMPANY` ne sont présents que sur le formulaire employeur.
 */
export const ZOHO_FIELD_CONTACT = 'contactId';
export const ZOHO_FIELD_EMAIL = 'email';
export const ZOHO_FIELD_PHONE = 'phone';
export const ZOHO_FIELD_COMPANY = 'cf_administrateur_de';

@Injectable({
  providedIn: 'root',
})
export class ZohoService {
  /**
   * Initialise le prefill des champs du widget d'aide Zoho ASAP (ZohoDeskAsap).
   *
   * Le script du widget est chargé côté GTM ; on ne gère ici que le shim `ZohoDeskAsapReady`
   * et le prefill des valeurs via `ZohoDeskAsap.set('ticket.form.prefillValues', ...)`.
   *
   * @param formKey clé du formulaire ciblé (`<departmentId>&&&<layoutId>`), propre à l'app appelante.
   */
  initZoho(user: User, formKey: string, company?: CompanyAdministratorSubscription): void {
    this.readyZoho();
    this.zohoUser(user, formKey, company);
  }

  private zohoUser(user: User, formKey: string, company?: CompanyAdministratorSubscription): void {
    if (user && company) {
      // @ts-expect-error: because of window
      window['ZohoDeskAsapReady'](() => {
        // @ts-expect-error: because of window
        window['ZohoDeskAsap'].set('ticket.form.prefillValues', {
          [formKey]: this.getZohoUserVariables(user, company),
        });
      });
    }
  }

  private getZohoUserVariables(user: User, company?: CompanyAdministratorSubscription) {
    let baseInfos: object = {
      [ZOHO_FIELD_CONTACT]: {
        defaultValue: `${user.profile.firstname} ${user.profile.lastname}`,
      },
      [ZOHO_FIELD_EMAIL]: {
        defaultValue: user.profile.mail,
      },
      [ZOHO_FIELD_PHONE]: {
        defaultValue: user.profile.mobile,
      },
    };
    if (company) {
      baseInfos = {
        ...baseInfos,
        [ZOHO_FIELD_COMPANY]: {
          defaultValue: company.companyName,
        },
      };
    }

    return baseInfos;
  }

  private readyZoho(): void {
    window['ZohoDeskAsapReady'] =
      window['ZohoDeskAsapReady'] ||
      function (o: unknown): void {
        if (
          ((window['ZohoDeskAsap__asyncalls'] = window['ZohoDeskAsap__asyncalls'] || []),
          window['ZohoDeskAsapReadyStatus'])
        ) {
          // @ts-expect-error: because of window
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          o && window['ZohoDeskAsap__asyncalls'].push(o);
          // @ts-expect-error: because of window
          for (let a = window['ZohoDeskAsap__asyncalls'], s = 0; s < a.length; s++) {
            // eslint-disable-next-line
            // @ts-ignore
            const n = a[s];
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            n && n();
          }
          window['ZohoDeskAsap__asyncalls'] = null;
        } else {
          // @ts-expect-error: because of window
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          o && window['ZohoDeskAsap__asyncalls'].push(o);
        }
      };
  }
}
